import { toEthiopian } from 'ethiopian-calendar-new';
import store from '@/store';
import { CalendarSystem } from '@/utils/enums';
import {
  DATETIME_OPTIONS,
  DATE_OPTIONS,
  MONTH_YEAR_OPTIONS,
  MONTHS_ETHIOPIAN,
  MONTHS_ETHIOPIAN_ABBREV,
  DAYS_ABBREV,
  DAYS_OF_THE_WEEK_AMHARIC,
  MONTHS_ETHIOPIAN_AMHARIC,
} from '@/utils/const';

function getCalendarSystem(): string {
  const account = (store.getters as { account?: { data?: { organization?: { calendarSystem?: string } } } }).account;
  return account?.data?.organization?.calendarSystem || CalendarSystem.GREGORIAN;
}

function formatEthiopianDate(date: Date, options?: { weekday?: boolean; monthLong?: boolean }) {
  const eth = toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate());
  const weekday = options?.weekday ? `${DAYS_OF_THE_WEEK_AMHARIC[date.getDay()]}, ` : '';
  const monthName = options?.monthLong ? MONTHS_ETHIOPIAN_AMHARIC[eth.month - 1] : MONTHS_ETHIOPIAN_ABBREV[eth.month - 1];
  return `${weekday}${monthName} ${eth.day}, ${eth.year}`;
}

const filters = {
  firstLetter(value: string) {
    return value != null ? value[0] : '';
  },

  capitalize(value: string) {
    return value != null ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  },

  uppercase(value: string) {
    return value != null ? value.toString().toUpperCase() : '';
  },

  getDateOnly(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      return formatEthiopianDate(date, { weekday: true, monthLong: true });
    }
    return date.toLocaleDateString('en-US', DATE_OPTIONS);
  },

  /** Compact date without weekday, e.g. "Mar 3, 2026" (or the Ethiopian equivalent). */
  getShortDate(value: string) {
    if (!value) return '-';
    const raw = String(value);
    const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T00:00:00`) : new Date(raw);
    if (Number.isNaN(date.getTime())) return '-';
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      return formatEthiopianDate(date, { weekday: false, monthLong: true });
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  },

  getDateOnlyDDMMYYYY(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      const eth = toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(eth.day)}/${pad(eth.month)}/${eth.year}`;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  },

  toLocalDateString(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      const eth = toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${eth.year}-${pad(eth.month)}-${pad(eth.day)}`;
    }
    return date.toLocaleString('sv-SE');
  },

  getDateOnlyInNumberFormat(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      const eth = toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${pad(eth.day)}/${pad(eth.month)}/${eth.year}`;
    }
    return new Intl.DateTimeFormat('en-GB').format(date);
  },

  getDayDifference(startDate: string, endDate: string): string {
    const d1 = new Date(startDate);
    const d2 = new Date(endDate);

    // Convert both dates to UTC to avoid time zone issues
    const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

    // Calculate difference in milliseconds and convert to days
    const msPerDay = 1000 * 60 * 60 * 24;
    const dayDifference = Math.floor((utc2 - utc1) / msPerDay);

    // Return the result with "day" or "days" depending on the value
    return `${dayDifference} ${dayDifference === 1 ? 'day' : 'days'}`;
  },

  getDayDifferenceNumber(startDate: string, endDate: string): number {
    const d1 = new Date(startDate);
    const d2 = new Date(endDate);

    // Convert both dates to UTC to avoid time zone issues
    const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

    // Calculate difference in milliseconds and convert to days
    const msPerDay = 1000 * 60 * 60 * 24;
    const dayDifference = Math.floor((utc2 - utc1) / msPerDay);

    // Return the result with "day" or "days" depending on the value
    return dayDifference;
  },

  getMonthAndYear(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      const eth = toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const monthName = MONTHS_ETHIOPIAN[eth.month - 1];
      return `${monthName} ${eth.year}`;
    }
    return date.toLocaleDateString('en-US', MONTH_YEAR_OPTIONS);
  },

  getTimeOnly(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
      : '-';
  },

  getTimeOnly24Hrs(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      : '-';
  },

  formatTo12Hour(timeStr: string): string {
    if (!timeStr) return '';
    const [hourStr, minute] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
  },

  toGMTHHMMSS(timeStr: string) {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    
    if (!timeStr) return null;
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(timeStr).trim());
    if (!m) return null;
    const h = Number(m[1]), min = Number(m[2]), s = m[3] ? Number(m[3]) : 0;

    // Build a local Date at today's date, then read UTC parts
    const base = new Date();
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, min, s, 0);
    return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`;
  },

  toLocalHHMMSS(timeStr: string) {
    const pad2 = (n: number) => String(n).padStart(2, '0');

    if (!timeStr) return null;
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(timeStr).trim());
    if (!m) return null;

    const h = Number(m[1]);
    const min = Number(m[2]);
    const s = m[3] ? Number(m[3]) : 0;

    // Build a Date in UTC based on today's date and given UTC time
    const base = new Date();
    const d = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), h, min, s, 0));

    // Extract LOCAL hours, minutes, seconds
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  },

  dateToLocalDateTime(timeStr: string) {
    const now = new Date();

    const [h, m, s] = timeStr.split(':').map(Number);
    return new Date(Date.UTC(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      h || 0,
      m || 0,
      s || 0
    ));
  },

  getDateAndTime(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const dateStr = formatEthiopianDate(date, { weekday: true, monthLong: true });
      return `${dateStr}, ${timeStr}`;
    }
    return date.toLocaleDateString('en-US', DATETIME_OPTIONS);
  },

  formatTimeToHHMM(value: string) {
    if (!value) return '';
    const [hourStr, minuteStr] = value.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr.padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // convert 0 or 12+ to 12-hour format
    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
  },

  checkDateRange(startDate: Date, endDate: Date): number {
    const now = new Date();
    if (now > endDate) {
      return 0; // Past
    } else if (now > startDate && now < endDate) {
      return 1; // Within the range
    } else if (now < startDate) {
      return 0;
    } else {
      return 0;
    }
  },

  formatDate(value: string) {
    if (!value) return '';
    const d = new Date(value);
    if (getCalendarSystem() === CalendarSystem.ETHIOPIC) {
      const eth = toEthiopian(d.getFullYear(), d.getMonth() + 1, d.getDate());
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${eth.year}-${pad(eth.month)}-${pad(eth.day)}`;
    }
    let month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  },

  formatDateDifference(targetDate: string): string {
    const now = new Date();
    const target = new Date(targetDate);

    // Calculate the total difference in days
    const msPerDay = 1000 * 60 * 60 * 24;
    const totalDaysDifference = Math.floor((target.getTime() - now.getTime()) / msPerDay);

    let remainingDays = Math.abs(totalDaysDifference);

    const years = Math.floor(remainingDays / 365);

    remainingDays %= 365;
    const months = Math.floor(remainingDays / 30);
    const days = remainingDays % 30;

    return `${years}y . ${months}m . ${days}d`;
  },

  formatPrice(value: number, currencyISO3: string | null) {
    let currencyObj: Intl.NumberFormatOptions = {
      style: 'currency',
      ...(currencyISO3 != null ? { currency: currencyISO3 } : {}),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };

    const formatter = new Intl.NumberFormat('en-US', currencyObj);
    return value != null ? formatter.format(value) : '-';
  },

  round(value: number) {
    return value.toFixed(2);
  },

  capitalizedFirstLetter(str: string) {
    if (!str) return '';
    return str.charAt(0).toUpperCase();
  },

  formatNumberWithComma(number: number) {
    if (isNaN(number)) {
      return 'Invalid number';
    }
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  formatPhoneNumber(phoneNumber: string) {
    // Remove any non-digit characters from the input
    const cleanNumber = phoneNumber.replace(/\D/g, '');

    // Check if the cleaned number has at least 10 digits
    if (cleanNumber.length >= 10) {
      // Use the replace method with a regular expression to format the string
      const formatted = cleanNumber
        .replace(/(\d{3})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4');
      return `+${formatted}`; // Prepend the country code +251
    } else {
      return 'Invalid number'; // Return error message if less than 10 digits
    }
  },

  formatToHHMM(timeStr: string) {
    if (!timeStr) return '';
    const [hourStr, minuteStr] = timeStr.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr.padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12; // convert to 12-hour clock
    return `${hour.toString().padStart(2, '0')}:${minute} ${ampm}`;
  },

  calculateTotalHours(dateStrings: string[]): number {
    if (dateStrings.length < 2) {
      return 0; // If there are fewer than 2 dates, there are no intervals to calculate
    }

    const dailyHours: { [key: string]: number } = {};

    for (let i = 0; i < dateStrings.length - 1; i += 2) {
      const start = new Date(dateStrings[i]);
      const end = new Date(dateStrings[i + 1]);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 0; // If any date is invalid, return 0
      }

      const startDateKey = start.toISOString().split("T")[0]; // Extract YYYY-MM-DD
      const endDateKey = end.toISOString().split("T")[0]; // Extract YYYY-MM-DD

      if (startDateKey === endDateKey) {
        // Both times fall on the same day
        const differenceInMilliseconds = end.getTime() - start.getTime();
        const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
        dailyHours[startDateKey] = (dailyHours[startDateKey] || 0) + differenceInHours;
      } else {
        // Spans multiple days
        const endOfStartDay = new Date(startDateKey);
        endOfStartDay.setUTCHours(23, 59, 59, 999);

        const startOfEndDay = new Date(endDateKey);
        startOfEndDay.setUTCHours(0, 0, 0, 0);

        const hoursOnStartDay = (endOfStartDay.getTime() - start.getTime()) / (1000 * 60 * 60);
        const hoursOnEndDay = (end.getTime() - startOfEndDay.getTime()) / (1000 * 60 * 60);

        dailyHours[startDateKey] = (dailyHours[startDateKey] || 0) + hoursOnStartDay;
        dailyHours[endDateKey] = (dailyHours[endDateKey] || 0) + hoursOnEndDay;
      }
    }

    // Sum up all daily totals
    const totalHours = Object.values(dailyHours).reduce((sum, hours) => sum + hours, 0);

    return Math.round(totalHours * 100) / 100; // Round to 2 decimal places
  },

  getHourMinuteDifference(date1: string | Date, date2: string | Date) {
    // Convert strings to Date objects
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // Get the difference in milliseconds
    const diffMs = Math.abs(d2.getTime() - d1.getTime());

    // Convert to hours and minutes
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
  }

};

export default filters;
